import { useRef, useEffect } from 'react'
import { MessageBubble, formatDate } from './MessageBubble'
import type { ChatMessage } from '../../services/chat'
import { ChatCircle, Spinner } from '@phosphor-icons/react'

interface MessageListProps {
  messages: ChatMessage[]
  loading?: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  onDeleteMessage?: (messageId: string) => void
  deleting?: boolean
  highlightMessageId?: string | null
}

export function MessageList({ messages, loading, onLoadMore, hasMore, onDeleteMessage, deleting, highlightMessageId }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const prevScrollHeightRef = useRef<number>(0)
  const highlightedRef = useRef<HTMLDivElement>(null)
  const hasScrolledToHighlight = useRef(false)

  // Scroll to highlighted message if provided, otherwise scroll to bottom
  useEffect(() => {
    if (highlightMessageId && !hasScrolledToHighlight.current && messages.length > 0) {
      // Find the highlighted message
      const messageIndex = messages.findIndex(m => m.id === highlightMessageId)
      if (messageIndex !== -1) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          highlightedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          hasScrolledToHighlight.current = true
        }, 100)
      }
    } else if (!highlightMessageId && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length, highlightMessageId])

  // Reset scroll flag when highlightMessageId changes
  useEffect(() => {
    hasScrolledToHighlight.current = false
  }, [highlightMessageId])

  // Handle scroll to top for loading more
  const handleScroll = () => {
    if (!containerRef.current || !onLoadMore || !hasMore || loading) return

    if (containerRef.current.scrollTop === 0) {
      prevScrollHeightRef.current = containerRef.current.scrollHeight
      onLoadMore()
    }
  }

  // Maintain scroll position after loading more
  useEffect(() => {
    if (containerRef.current && prevScrollHeightRef.current > 0) {
      const newScrollHeight = containerRef.current.scrollHeight
      containerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current
      prevScrollHeightRef.current = 0
    }
  }, [messages])

  // Group messages by date
  const groupedMessages: { date: string; messages: ChatMessage[] }[] = []
  let currentDate = ''

  messages.forEach((message) => {
    const messageDate = new Date(message.createdAt).toDateString()
    if (messageDate !== currentDate) {
      currentDate = messageDate
      groupedMessages.push({
        date: message.createdAt,
        messages: [message]
      })
    } else {
      groupedMessages[groupedMessages.length - 1].messages.push(message)
    }
  })

  if (messages.length === 0 && !loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <ChatCircle size={64} className="mx-auto text-gray-300 mb-4" weight="light" />
          <p className="text-gray-500 font-medium">Nenhuma mensagem ainda</p>
          <p className="text-gray-400 text-sm mt-1">
            Inicie a conversa enviando uma mensagem
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
      onScroll={handleScroll}
    >
      {/* Loading indicator at top */}
      {loading && hasMore && (
        <div className="flex justify-center py-2">
          <Spinner size={24} className="animate-spin text-gray-400" />
        </div>
      )}

      {/* Messages grouped by date */}
      {groupedMessages.map((group, groupIndex) => (
        <div key={groupIndex}>
          {/* Date separator */}
          <div className="flex items-center justify-center my-4">
            <span className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full">
              {formatDate(group.date)}
            </span>
          </div>

          {/* Messages */}
          <div className="space-y-3">
            {group.messages.map((message) => {
              const isHighlighted = highlightMessageId === message.id
              return (
                <div
                  key={message.id}
                  ref={isHighlighted ? highlightedRef : undefined}
                  className={`transition-all duration-500 ${
                    isHighlighted ? 'ring-2 ring-emerald-400 ring-offset-2 rounded-lg animate-pulse-once' : ''
                  }`}
                >
                  <MessageBubble
                    message={message}
                    onDelete={onDeleteMessage}
                    deleting={deleting}
                  />
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* Loading indicator for initial load */}
      {loading && messages.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <Spinner size={32} className="animate-spin text-emerald-500" />
          <span className="ml-3 text-gray-500">Carregando mensagens...</span>
        </div>
      )}
    </div>
  )
}
